import { zodResolver } from "@hookform/resolvers/zod";
import {
	Button,
	Group,
	MultiSelect,
	NumberInput,
	Select,
	Stack,
	Switch,
	Text,
	TextInput,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { DiscountKind } from "@/features/discounts/api/types";
import { KIND_OPTIONS } from "@/features/discounts/lib/kind-options";
import {
	type OfferFormValues,
	offerSchemaFor,
} from "@/features/discounts/lib/offer-schema";
import { parseIsoDate, toIsoDate } from "@/shared/lib/iso-date";

export function OfferEditorForm({
	defaults,
	existingId,
	knownCategories,
	partnerOptions,
	positions,
	saving,
	onCancel,
	onSubmit,
}: {
	defaults: OfferFormValues;
	existingId?: string;
	knownCategories: { value: string; label: string }[];
	partnerOptions: { value: string; label: string }[];
	positions: { id: string; name: string }[];
	saving: boolean;
	onCancel: () => void;
	onSubmit: (values: OfferFormValues) => void;
}) {
	const { t } = useTranslation();
	const { control, handleSubmit, setValue, watch } = useForm<OfferFormValues>({
		resolver: zodResolver(offerSchemaFor(defaults.endsOn)),
		defaultValues: defaults,
	});
	const type = watch("type");
	const today = parseIsoDate(toIsoDate(new Date())) ?? undefined;

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<Stack>
				<Controller
					name="name"
					control={control}
					render={({ field, fieldState }) => (
						<TextInput
							{...field}
							label={t("discounts.titleField")}
							error={fieldState.error?.message}
						/>
					)}
				/>
				<Controller
					name="type"
					control={control}
					render={({ field }) => (
						<Select
							label={t("discounts.kind")}
							data={KIND_OPTIONS}
							disabled={Boolean(existingId)}
							value={field.value}
							onChange={(value) => {
								const next = (value as DiscountKind) ?? "NATIONAL";
								field.onChange(next);
								setValue("categoryIds", []);
								setValue("partnerIds", []);
								setValue("endsOn", "");
							}}
						/>
					)}
				/>
				<Controller
					name="promoCode"
					control={control}
					render={({ field }) => (
						<TextInput {...field} label={t("discounts.promoCode")} />
					)}
				/>
				<Controller
					name="isActive"
					control={control}
					render={({ field }) => (
						<Switch
							label={t("common.active")}
							checked={field.value}
							onChange={(event) => field.onChange(event.currentTarget.checked)}
						/>
					)}
				/>
				{type === "CORPORATE" ? (
					<Controller
						name="partnerIds"
						control={control}
						render={({ field }) => (
							<MultiSelect
								label={t("partners.title")}
								data={partnerOptions}
								value={field.value}
								onChange={field.onChange}
							/>
						)}
					/>
				) : null}
				{type === "NATIONAL" || type === "FAMILY" ? (
					<Controller
						name="categoryIds"
						control={control}
						render={({ field }) => (
							<MultiSelect
								label={t("discounts.categories")}
								description={t("discounts.categoriesHint")}
								data={knownCategories}
								value={field.value}
								onChange={field.onChange}
							/>
						)}
					/>
				) : null}
				{type === "EVERYONE" ? (
					<Controller
						name="endsOn"
						control={control}
						render={({ field, fieldState }) => (
							<DateInput
								label={t("discounts.endsOn")}
								description={t("discounts.endsOnHint")}
								placeholder={t("discounts.endsOnPlaceholder")}
								valueFormat="DD.MM.YYYY"
								clearable
								minDate={today}
								error={fieldState.error?.message}
								value={field.value ? parseIsoDate(field.value) : null}
								onChange={(value) =>
									field.onChange(value ? toIsoDate(value) : "")
								}
							/>
						)}
					/>
				) : null}
				<Text size="sm" fw={500}>
					{t("discounts.rates")}
				</Text>
				{positions.map((position) => (
					<Controller
						key={position.id}
						name={`rates.${position.id}`}
						control={control}
						render={({ field }) => (
							<NumberInput
								label={position.name}
								min={0}
								max={100}
								hideControls
								placeholder={t("common.dash")}
								value={field.value === "" ? "" : field.value}
								onChange={(value) =>
									field.onChange(typeof value === "number" ? value : "")
								}
							/>
						)}
					/>
				))}
				<Group justify="flex-end">
					<Button variant="default" type="button" onClick={onCancel}>
						{t("common.cancel")}
					</Button>
					<Button type="submit" loading={saving}>
						{t("common.save")}
					</Button>
				</Group>
			</Stack>
		</form>
	);
}
